import FetchService from '../../../core/services/FetchService';
import FormService from '../../../core/services/FormService';
import { isRequired } from '../../../core/validations';
import { action, extendObservable, toJS } from 'mobx';
import ContractProductsService from './ContractProductsService';
import ConfirmationDialogService from '../../../core/services/ConfirmationDialogService';
import NotificationService from '../../../core/services/NotificationService';
import { browserHistory } from 'react-router';
import { head, omit, orderBy } from 'lodash';
import moment from 'moment/moment';
import { CONTRACT_PRODUCTS } from '../../../core/consts';

class ReactivateContractFormService {
  fetch = new FetchService();

  submit = new FetchService();

  form = new FormService();

  productsService = new ContractProductsService();

  constructor() {
    extendObservable(this, {
      contract: {},
    });
  }

  init = (contractId) => {
    this.contractId = contractId;
    this.form.setInitialValues({
      type: null,
      startsAt: null,
      endsAt: null,
      dueDay: null,
    });
    this.form.validations = {};
    this.load(contractId);
  };

  verifyAndSetDebitsProduct = action(async (contractId) => {
    const overdueBills = await this.fetch.fetch({
      apiVersion: 'v2',
      url: `/contracts/${contractId}/overdue-bills`,
    });
    if (overdueBills.length) {
      let debitProductRowKey = toJS(this.productsService.contractProducts).length + 1;
      this.debtsProduct = {
        hs_sku: CONTRACT_PRODUCTS.OTHERS,
        description: 'Débitos',
        finalUnitPrice: overdueBills.reduce((total, overdueBill) => {
          total += overdueBill.total;
          return total;
        }, 0),
        isDebitProduct: true,
      };
      this.productsService.contractProducts.push({ rowKey: debitProductRowKey, ...this.debtsProduct });
    }
  });

  load = action(async (contractId) => {
    this.contractId = contractId;
    this.contract = await this.fetch.fetch({ url: `/contracts/${this.contractId}`, apiVersion: 'v2' });
    if (this.contract) {
      this.contractType = this.contract.type || (this.contract.isUpfront ? 'UPFRONT' : 'POST_PAYMENT');
      this.form.setValue('type', this.contractType);
      this.updateValidations(this.contractType);
      this.productsService.init(this.contractType);
      await this.productsService.load();
      await this.verifyAndSetDebitsProduct(contractId);
    }
  });

  onChange = action(async (key, value) => {
    if (key === 'type') {
      this.productsService.type = value;
      this.productsService.contractProducts = [];
      await this.productsService.load();
      this.form.setValues({
        startsAt: null,
        endsAt: null,
        dueDay: null,
      });
      this.form.setPristine('startsAt');
      this.form.setPristine('dueDay');
      this.form.submitted = false;
      this.updateValidations(value);
      if (this.debtsProduct) {
        let debitProductRowKey = toJS(this.productsService.contractProducts).length + 1;
        this.productsService.contractProducts.push({ rowKey: debitProductRowKey, ...this.debtsProduct });
      }
    }
    this.form.setValue(key, value);
  });

  updateValidations = action((value) => {
    if (value === 'POST_PAYMENT') {
      this.form.validations = {
        startsAt: [isRequired],
        dueDay: [isRequired],
      };
    } else {
      this.form.validations = {};
    }
  });

  onSubmit = () => {
    this.form.setSubmitted();
    if (!this.form.errors) {
      ConfirmationDialogService.show(
        'Reactivate Contract',
        'Are you sure you want to reactivate the contract?',
        async () => {
          this.submit
            .fetch({
              apiVersion: 'v3',
              method: 'PUT',
              url: `/contracts/${this.contractId}/reactivate`,
              body: {
                type: this.form.getValue('type'),
                ...(this.form.getValue('startsAt') && {
                  startsAt: this.form.getValue('startsAt').utc().startOf('month'),
                }),
                ...(this.form.getValue('endsAt') && {
                  endsAt: this.form.getValue('endsAt').utc().endOf('month'),
                }),
                ...(this.form.getValue('dueDay') && {
                  dueDay: Number(this.form.getValue('dueDay')),
                }),
                products: toJS(this.productsService.contractProducts)
                  .filter((p) => p.isEnabled)
                  .map((product) => ({
                    ...omit(product, ['rowKey', 'id']),
                    hs_sku: product.hs_sku,
                    description: product.description,
                    finalUnitPrice: Number(product.finalUnitPrice),
                    ...(product.amount && {
                      amount: Number(product.amount),
                    }),
                    ...(product.freeAmount && {
                      freeAmount: Number(product.freeAmount),
                    }),
                    ...(product.installments && {
                      installments: Number(product.installments),
                    }),
                    ...(product.paidInstallments && {
                      paidInstallments: Number(product.paidInstallments),
                    }),
                    ...(product.discount && {
                      discount: Number(product.discount),
                    }),
                  })),
              },
            })
            .then(() => {
              if (this.submit.data) {
                this.submit
                  .fetch({
                    method: 'get',
                    url: `/bills?page=1&documentNumber=${this.submit.data.documentNumber}`,
                    apiVersion: 'v2',
                  })
                  .then(() => {
                    NotificationService.addNotification(`Contract successfully reactivated.`, 'success');
                    if (this.submit.data.total > 0) {
                      const lastBill = head(orderBy(this.submit.data.docs, ['createdAt'], 'desc'));
                      const isOpenBill = lastBill.status === 'OPEN';
                      const isCreatedToday = moment(lastBill.createdAt).isSameOrAfter(moment().startOf('day'));
                      if (isOpenBill && isCreatedToday) {
                        browserHistory.push(`billing/${lastBill.id}/details`);
                      } else {
                        browserHistory.push('/contracts');
                      }
                    } else {
                      browserHistory.push('/contracts');
                    }
                    if (this.submit.error) {
                      browserHistory.push('/contracts');
                    }
                  });
              }
              if (this.submit.error) {
                if (
                  [
                    'contract_start_invalid',
                    'products_not_allowed',
                    'contract_type_products_missing',
                    'contract_missing_debts_product',
                    'contract_debts_product_value_invalid',
                  ].includes(this.submit.response.data.error)
                ) {
                  NotificationService.addNotification(this.submit.error, 'error');
                } else {
                  NotificationService.addNotification(this.submit.error || `Error reactivating contract.`, 'error');
                }
              }
            });
        }
      );
    }
  };

  onReset = action(() => {
    this.form.reset();
    this.productsService.contractProducts = [{ ...this.debtsProduct }];
    this.form.setValue('type', this.contractType);
  });
}

const reactivateContractFormService = new ReactivateContractFormService();

export default reactivateContractFormService;
